import React, {lazy, Suspense} from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import {customDispatch} from "../utils/customDispatch.js";
import {history} from "../utils/history";
import {useDispatch, useSelector} from "react-redux";
import HonorariumManagement from "../pages/HonorariumManagement";
import HonorariumDetails from "../pages/HonorariumDetails";
import {ROLES} from "../utils/constants.js";

const USER_MANAGEMENT_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.PROGRAM_MANAGER,
  ROLES.SUPERVISOR,
];

const SCHOOL_MANAGEMENT_ROLES = [ROLES.SUPER_ADMIN, ROLES.MANAGING_DIRECTOR];

export const ATTENDANCE_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.PROGRAM_MANAGER,
  ROLES.SUPERVISOR,
  ROLES.TEACHER,
  ROLES.ACCOUNTANT,
  ROLES.OFFICE_STAFF,
];

const HONORARIUM_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.ACCOUNTANT,
];

const ADMIN_FINANCE_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.ACCOUNTANT,
  ROLES.MANAGING_DIRECTOR,
];

const SCHOLARSHIP_ROLES = [
  ROLES.SUPER_ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.OFFICE_STAFF,
];

const APPROVAL_ROLES = [ROLES.MANAGING_DIRECTOR];

// Phase 8: the "My Honorarium" view is reachable by every role that draws
// a honorarium (Teacher / Supervisor / PM / Accountant / Office Staff) plus
// SA + MD for admin / approver access.
const TEACHER_HONORARIUM_ROLES = [
  ROLES.TEACHER,
  ROLES.SUPER_ADMIN,
  ROLES.MANAGING_DIRECTOR,
  ROLES.PROGRAM_MANAGER,
  ROLES.SUPERVISOR,
  ROLES.ACCOUNTANT,
  ROLES.OFFICE_STAFF,
];

const Login = lazy(() => import("../pages/Login.js"));
const ForgotPassword = lazy(() => import("../pages/ForgotPassword.js"));
const ResetPassword = lazy(() => import("../pages/ResetPassword.js"));
const DashboardLayout = lazy(() =>
  import("../components/layout/DashboardLayout.js")
);
const UserManagement = lazy(() => import("../pages/UserManagement.js"));
const AddUser = lazy(() => import("../pages/AddUser.js"));
const TeacherAttendance = lazy(() =>
  import("../pages/AttendanceManagement.js")
);
const UserDetails = lazy(() => import("../pages/UserDetails.js"));
const SchoolManagement = lazy(() => import("../pages/SchoolManagement.js"));
const Profile = lazy(() => import("../pages/Profile.js"));
const HolidayManagement = lazy(() => import("../pages/HolidayManagement.js"));
const ExpenseTable = lazy(() => import("../pages/ExpenseTable.js"));
const ExpenseForm = lazy(() => import("../pages/ExpenseForm.js"));
const ExpenseDetails = lazy(() => import("../pages/ExpenseDetails.js"));
const Config = lazy(() => import("../pages/Config.js"));
const AccountantExpenses = lazy(() => import("../pages/AccountantExpenses.js"));
const MonthlyExpenseDetails = lazy(() =>
  import("../pages/MonthlyExpenseDetails.js")
);
const TeacherHonorarium = lazy(() => import("../pages/TeacherHonorarium.js"));
const ScholarshipList = lazy(() => import("../pages/ScholarshipList.js"));
const ScholarshipDetails = lazy(() => import("../pages/ScholarshipDetails.js"));
const ScholarshipForm = lazy(() => import("../pages/ScholarshipForm.js"));
const FundManagement = lazy(() => import("../pages/FundManagement.js"));
const AddTransaction = lazy(() => import("../pages/AddTransaction.js"));
const NotificationList = lazy(() => import("../pages/NotificationList.js"));
const AttendanceDetails = lazy(() => import("../pages/AttendanceDetails.js"));

// Dashboard Components
const SuperAdminDashboard = lazy(() =>
  import("../components/dashboard/SuperAdminDashboard.js")
);
const ManagingDirectorDashboard = lazy(() =>
  import("../components/dashboard/ManagingDirectorDashboard.js")
);
const ProgramManagerDashboard = lazy(() =>
  import("../components/dashboard/ProgramManagerDashboard.js")
);
const SupervisorDashboard = lazy(() =>
  import("../components/dashboard/SupervisorDashboard.js")
);
const TeacherDashboard = lazy(() =>
  import("../components/dashboard/TeacherDashboard.js")
);
const AccountantDashboard = lazy(() =>
  import("../components/dashboard/AccountantDashboard.js")
);
const OfficeStaffDashboard = lazy(() =>
  import("../components/dashboard/OfficeStaffDashboard.js")
);
const ApprovalRequestsPage = lazy(() =>
  import("../pages/ApprovalRequestsPage.js")
);

export const ProtectedRoute = ({children, roles, requireScholarshipAccess}) => {
  const {isAuthenticated, userData} = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (roles && !roles.includes(userData?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    requireScholarshipAccess &&
    userData?.role === ROLES.OFFICE_STAFF &&
    !userData?.canManageScholarships
  ) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export const RoleBasedDashboardRouter = () => {
  const {userData} = useSelector((state) => state.auth);

  switch (userData?.role) {
    case ROLES.MANAGING_DIRECTOR:
      return <ManagingDirectorDashboard />;
    case ROLES.SUPER_ADMIN:
      return <SuperAdminDashboard />;
    case ROLES.PROGRAM_MANAGER:
      return <ProgramManagerDashboard />;
    case ROLES.SUPERVISOR:
      return <SupervisorDashboard />;
    case ROLES.TEACHER:
      return <TeacherDashboard />;
    case ROLES.ACCOUNTANT:
      return <AccountantDashboard />;
    case ROLES.OFFICE_STAFF:
      return <OfficeStaffDashboard />;
    default:
      return <Navigate to="/" />;
  }
};

export const AppFallbackRoute = () => {
  const {isAuthenticated} = useSelector((state) => state.auth);

  return <Navigate to={isAuthenticated ? "/dashboard" : "/"} replace />;
};

function Router() {
  history.navigate = useNavigate();
  history.location = useLocation();
  customDispatch.dispatch = useDispatch();

  return (
    <Suspense fallback={<div className="loading"></div>}>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
          <Route index element={<RoleBasedDashboardRouter />} />

          {/* Additional Nested Routes */}
          <Route path="notifications" element={<NotificationList />} />
          <Route
            path="users"
            element={
              <ProtectedRoute roles={USER_MANAGEMENT_ROLES}>
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="users/add"
            element={
              <ProtectedRoute roles={USER_MANAGEMENT_ROLES}>
                <AddUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="users/:userId"
            element={
              <ProtectedRoute roles={USER_MANAGEMENT_ROLES}>
                <UserDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="users/edit/:userId"
            element={
              <ProtectedRoute roles={USER_MANAGEMENT_ROLES}>
                <AddUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="schools"
            element={
              <ProtectedRoute roles={SCHOOL_MANAGEMENT_ROLES}>
                <SchoolManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="attendance"
            element={
              <ProtectedRoute roles={ATTENDANCE_ROLES}>
                <TeacherAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="attendance/:date"
            element={
              <ProtectedRoute roles={ATTENDANCE_ROLES}>
                <AttendanceDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="attendance/:date/:userId"
            element={
              <ProtectedRoute roles={ATTENDANCE_ROLES}>
                <AttendanceDetails />
              </ProtectedRoute>
            }
          />
          <Route path="profile" element={<Profile />} />
          <Route
            path="approvals"
            element={
              <ProtectedRoute roles={APPROVAL_ROLES}>
                <ApprovalRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="holiday"
            element={
              <ProtectedRoute roles={SCHOOL_MANAGEMENT_ROLES}>
                <HolidayManagement />
              </ProtectedRoute>
            }
          />
          <Route path="expenses" element={<ExpenseTable />} />
          <Route path="expenses/add" element={<ExpenseForm />} />
          <Route path="expenses/:expenseId" element={<ExpenseDetails />} />
          <Route
            path="config"
            element={
              <ProtectedRoute roles={SCHOOL_MANAGEMENT_ROLES}>
                <Config />
              </ProtectedRoute>
            }
          />
          <Route
            path="honorarium"
            element={
              <ProtectedRoute roles={HONORARIUM_ROLES}>
                <HonorariumManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="honorarium/:honorariumId"
            element={
              <ProtectedRoute roles={HONORARIUM_ROLES}>
                <HonorariumDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="expense-management"
            element={
              <ProtectedRoute roles={ADMIN_FINANCE_ROLES}>
                <AccountantExpenses />
              </ProtectedRoute>
            }
          />
          <Route
            path="expense-management/:id"
            element={
              <ProtectedRoute roles={ADMIN_FINANCE_ROLES}>
                <MonthlyExpenseDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="teacherhonorarium"
            element={
              <ProtectedRoute roles={TEACHER_HONORARIUM_ROLES}>
                <TeacherHonorarium />
              </ProtectedRoute>
            }
          />
          <Route
            path="scholarship"
            element={
              <ProtectedRoute
                roles={SCHOLARSHIP_ROLES}
                requireScholarshipAccess
              >
                <ScholarshipList />
              </ProtectedRoute>
            }
          />
          <Route
            path="scholarship/:scholarshipId"
            element={
              <ProtectedRoute
                roles={SCHOLARSHIP_ROLES}
                requireScholarshipAccess
              >
                <ScholarshipDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="scholarship/edit/:scholarshipId"
            element={
              <ProtectedRoute
                roles={SCHOLARSHIP_ROLES}
                requireScholarshipAccess
              >
                <ScholarshipForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="scholarship/add"
            element={
              <ProtectedRoute
                roles={SCHOLARSHIP_ROLES}
                requireScholarshipAccess
              >
                <ScholarshipForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="fund-management"
            element={
              <ProtectedRoute roles={ADMIN_FINANCE_ROLES}>
                <FundManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="fund-management/:accountId/add-transaction"
            element={
              <ProtectedRoute roles={ADMIN_FINANCE_ROLES}>
                <AddTransaction />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
        <Route path="*" element={<AppFallbackRoute />} />
      </Routes>
    </Suspense>
  );
}

export default Router;
